import { ConnectionListResult, FeatureFlagsContext, GridSortProps, PaginatedQueryWrapper } from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import {
  FlowDownstreamFlowsFilteredByFlowOutputFieldQuery,
  FlowDownstreamFlowsFilteredByFlowOutputFieldQuery_flows_outputFieldsConnection_nodes_downstreamFlowsConnection_nodes,
  FlowDownstreamFlowsFilteredByFlowOutputFieldQueryVariables,
} from '../../../gen-src/queries/FlowDownstreamFlowsFilteredByFlowOutputFieldQuery';
import {
  FlowDownstreamFlowsFilteredByFlowOutputFieldWithoutPersonalSpaceQuery,
  FlowDownstreamFlowsFilteredByFlowOutputFieldWithoutPersonalSpaceQuery_flows_outputFieldsConnection_nodes_downstreamFlowsConnection_nodes,
  FlowDownstreamFlowsFilteredByFlowOutputFieldWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/FlowDownstreamFlowsFilteredByFlowOutputFieldWithoutPersonalSpaceQuery';
import {
  FlowDownstreamFlowsQuery,
  FlowDownstreamFlowsQuery_flows_downstreamFlowsConnection_nodes,
  FlowDownstreamFlowsQueryVariables,
} from '../../../gen-src/queries/FlowDownstreamFlowsQuery';
import {
  FlowDownstreamFlowsWithoutPersonalSpaceQuery,
  FlowDownstreamFlowsWithoutPersonalSpaceQuery_flows_downstreamFlowsConnection_nodes,
  FlowDownstreamFlowsWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/FlowDownstreamFlowsWithoutPersonalSpaceQuery';
import { FlowSortOrder } from '../../../gen-src/queries/globalTypes';
import { FlowDownstreamFlowsFilteredByFlowOutputFieldQueryGql } from '../../queries/FlowDownstreamFlowsFilteredByFlowOutputFieldQueryGql';
import { FlowDownstreamFlowsFilteredByFlowOutputFieldWithoutPersonalSpaceQueryGql } from '../../queries/FlowDownstreamFlowsFilteredByFlowOutputFieldWithoutPersonalSpaceQueryGql';
import { FlowDownstreamFlowsQueryGql } from '../../queries/FlowDownstreamFlowsQueryGql';
import { FlowDownstreamFlowsWithoutPersonalSpaceQueryGql } from '../../queries/FlowDownstreamFlowsWithoutPersonalSpaceQueryGql';
import {
  FlowsDisplayGrid,
  FlowsDisplayGridRowData,
  getRowDataForFlow,
  getRowDataForFlowWithoutPersonalSpace,
} from '../sharedDisplayGrid/FlowsDisplayGrid';
import { FlowsValidSortFields } from '../sorting/sortProviders/FlowsGridSortProvider';

type QueryVariables = FlowDownstreamFlowsQueryVariables;
type QueryResults = FlowDownstreamFlowsQuery;
type QueryResultListNode = FlowDownstreamFlowsQuery_flows_downstreamFlowsConnection_nodes;
type GridRowData = FlowsDisplayGridRowData;

type QueryVariablesWithoutPersonalSpace = FlowDownstreamFlowsWithoutPersonalSpaceQueryVariables;
type QueryResultsWithoutPersonalSpace = FlowDownstreamFlowsWithoutPersonalSpaceQuery;
type QueryResultListNodeWithoutPersonalSpace = FlowDownstreamFlowsWithoutPersonalSpaceQuery_flows_downstreamFlowsConnection_nodes;

type QueryVariablesWithFilter = FlowDownstreamFlowsFilteredByFlowOutputFieldQueryVariables;
type QueryResultsWithFilter = FlowDownstreamFlowsFilteredByFlowOutputFieldQuery;
type QueryResultListNodeWithFilter =
  FlowDownstreamFlowsFilteredByFlowOutputFieldQuery_flows_outputFieldsConnection_nodes_downstreamFlowsConnection_nodes;

type QueryVariablesWithFilterWithoutPersonalSpace = FlowDownstreamFlowsFilteredByFlowOutputFieldWithoutPersonalSpaceQueryVariables;
type QueryResultsWithFilterWithoutPersonalSpace = FlowDownstreamFlowsFilteredByFlowOutputFieldWithoutPersonalSpaceQuery;
type QueryResultListNodeWithFilterWithoutPersonalSpace =
  FlowDownstreamFlowsFilteredByFlowOutputFieldWithoutPersonalSpaceQuery_flows_outputFieldsConnection_nodes_downstreamFlowsConnection_nodes;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  return flow ? flow.downstreamFlowsConnection : null;
}

function selectConnectionListResultWithoutPersonalSpace(resultData: QueryResultsWithoutPersonalSpace):
  ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  return flow ? flow.downstreamFlowsConnection : null;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  const flowOutputFields = flow && flow.outputFieldsConnection && flow.outputFieldsConnection.nodes;
  const field = flowOutputFields && flowOutputFields[0];
  return field ? field.downstreamFlowsConnection : null;
}

function selectConnectionListResultForFilteredQueryWithoutPersonalSpace(
  resultData: QueryResultsWithFilterWithoutPersonalSpace
): ConnectionListResult<QueryResultListNodeWithFilterWithoutPersonalSpace> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  const flowOutputFields = flow && flow.outputFieldsConnection && flow.outputFieldsConnection.nodes;
  const field = flowOutputFields && flowOutputFields[0];
  return field ? field.downstreamFlowsConnection : null;
}

export type FlowDownstreamFlowsQueryGridProps = GridSortProps<FlowsValidSortFields> & {
  readonly flowVizportalUrlId: string;
  readonly filteredFlowOutputFieldId: string | null;
  readonly rowCount: number | null;
};

export function FlowDownstreamFlowsQueryGrid(props: FlowDownstreamFlowsQueryGridProps): JSX.Element {
  const { personalSpaceEnabled } = React.useContext(FeatureFlagsContext);
  const emptyGridMessage = Messages.EmptyDownstreamFlowsLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredFlowsLineageGridMessage();

  return personalSpaceEnabled ? (
    props.filteredFlowOutputFieldId ? (
      // Query with Selected Field as Filter
      <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
        queryGql={FlowDownstreamFlowsFilteredByFlowOutputFieldQueryGql}
        partialQueryVariables={{
          flowVizportalUrlId: props.flowVizportalUrlId,
          selectedFlowOutputFieldId: props.filteredFlowOutputFieldId,
          orderBy: props.orderBy as FlowSortOrder,
        }}
        selectConnectionListResult={selectConnectionListResultForFilteredQuery}
        mapResultNode={getRowDataForFlow}
      >
        {({ rowStore }) => (
          <FlowsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyFilteredGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        )}
      </PaginatedQueryWrapper>
    ) : (
      // Query without Selection
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={FlowDownstreamFlowsQueryGql}
        partialQueryVariables={{
          flowVizportalUrlId: props.flowVizportalUrlId,
          orderBy: props.orderBy as FlowSortOrder,
        }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={getRowDataForFlow}
      >
        {({ rowStore }) => (
          <FlowsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        )}
      </PaginatedQueryWrapper>
    )
  ) : (
    props.filteredFlowOutputFieldId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilterWithoutPersonalSpace, QueryResultsWithFilterWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={FlowDownstreamFlowsFilteredByFlowOutputFieldWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{
          flowVizportalUrlId: props.flowVizportalUrlId,
          selectedFlowOutputFieldId: props.filteredFlowOutputFieldId,
          orderBy: props.orderBy as FlowSortOrder,
        }}
        selectConnectionListResult={selectConnectionListResultForFilteredQueryWithoutPersonalSpace}
        mapResultNode={getRowDataForFlowWithoutPersonalSpace}
      >
        {({ rowStore }) => (
          <FlowsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyFilteredGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        )}
      </PaginatedQueryWrapper>
    ) : (
      <PaginatedQueryWrapper<QueryVariablesWithoutPersonalSpace, QueryResultsWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={FlowDownstreamFlowsWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{
          flowVizportalUrlId: props.flowVizportalUrlId,
          orderBy: props.orderBy as FlowSortOrder,
        }}
        selectConnectionListResult={selectConnectionListResultWithoutPersonalSpace}
        mapResultNode={getRowDataForFlowWithoutPersonalSpace}
      >
        {({ rowStore }) => (
          <FlowsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        )}
      </PaginatedQueryWrapper>
    )
  );
}
